
import React from 'react';
import DOMPurify from 'dompurify';
import { decodeText } from '@/utils/textDecoding';

interface TextMessageProps {
  content: string;
  isUser: boolean;
}

const TextMessage: React.FC<TextMessageProps> = ({ content, isUser }) => {
  if (!content.trim()) return null;

  const decodedContent = decodeText(content);

  // Remove dealer name [[...]] and address [[[...]]] markers used for the maps card
  const cleanedContent = decodedContent
    .replace(/\[\[\[[^\]]+\]\]\]/g, '')
    .replace(/\[\[[^\]]+\]\]/g, '');

  // Helper to check if URL is on same domain
  const isSameDomain = (url: string): boolean => {
    try {
      const linkUrl = new URL(url, window.location.origin);
      return linkUrl.hostname === window.location.hostname;
    } catch {
      return false;
    }
  };

  // Helper to check if URL is a YouTube video link
  const isYouTubeUrl = (url: string): boolean => {
    return /^https?:\/\/(www\.)?(youtube\.com\/watch|youtu\.be\/)/i.test(url);
  };

  // Detect language for YouTube label
  const detectLang = (): 'fr' | 'en' => {
    try {
      const metaTag = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement;
      if (metaTag?.content?.toLowerCase().startsWith('fr')) return 'fr';
    } catch { /* ignore */ }
    return 'en';
  };
  const lang = detectLang();
  const youtubeLabel = lang === 'fr' ? 'Voir la vid\u00e9o' : 'View Video';
  const genericLinkLabel = lang === 'fr' ? 'Voir le lien' : 'View link';

  // Convert URLs to clickable links (must be done before other HTML processing)
  // Same-domain links open in current window, external links open in new tab
  // Links show a short label instead of the raw URL to avoid table-layout overflow
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
  let processedContent = cleanedContent.replace(urlRegex, (match, url) => {
    const sameDomain = isSameDomain(url);
    const targetAttr = sameDomain ? '' : ' target="_blank" rel="noopener noreferrer"';
    const displayText = isYouTubeUrl(url) ? youtubeLabel : genericLinkLabel;
    return `<a href="${url}"${targetAttr} class="text-blue-600 underline hover:text-blue-800 break-all [word-break:break-all] [overflow-wrap:anywhere] max-w-full inline-block align-bottom">${displayText}</a>`;
  });


  // Convert **text** to bold
  processedContent = processedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Parse → bullet points into proper list structure
  // Handle both \n and \r\n line endings
  const lines = processedContent.split(/\r?\n/);
  const parsedLines: string[] = [];
  let inList = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Check if line starts with → (after trimming whitespace)
    const isBullet = /^→\s*/.test(trimmedLine);
    
    if (isBullet) {
      if (!inList) {
        parsedLines.push('<ul class="chat-bullet-list">');
        inList = true;
      }
      // Extract text after the arrow and any whitespace
      const bulletText = trimmedLine.replace(/^→\s*/, '').trim();
      parsedLines.push(`<li>${bulletText}</li>`);
    } else {
      if (inList) {
        parsedLines.push('</ul>');
        inList = false;
      }
      // Only add non-empty lines or preserve original spacing
      if (trimmedLine || line === '') {
        parsedLines.push(line);
      }
    }
  }
  
  // Close list if still open at end
  if (inList) {
    parsedLines.push('</ul>');
  }
  
  processedContent = parsedLines.join('\n');
  
  // Convert newlines to HTML line breaks (but not inside lists)
  // Double newlines become paragraph breaks
  processedContent = processedContent.replace(/\n\n/g, '<br><br>');
  
  // Single newlines become single line breaks
  processedContent = processedContent.replace(/\n/g, '<br>');

  // Sanitize HTML content to prevent XSS attacks
  const sanitizeConfig = {
    ALLOWED_TAGS: ['br', 'p', 'strong', 'em', 'u', 'b', 'i', 'ul', 'li', 'div', 'a'],
    ALLOWED_ATTR: ['class', 'href', 'target', 'rel']
  };
  
  const sanitizedContent = DOMPurify.sanitize(processedContent, sanitizeConfig);

  return (
    <div className={`rounded-lg px-4 py-2 max-w-full min-w-0 mb-2 ${
      isUser
        ? 'bg-gray-600 text-white rounded-br-sm'
        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
    }`}>
      <div 
        className="whitespace-pre-wrap break-words"
        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    </div>
  );
};

export default TextMessage;